import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { t } from '@/lib/i18n'

export type ToastKind = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  kind: ToastKind
  message: string
}

interface ToastContextValue {
  toast: (message: string, kind?: ToastKind) => void
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

// useToast returns the imperative toast API. Most call sites only need
// toast.error(err.message) on a mutation onError handler.
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}

const TOAST_TTL_MS = 5_000
let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
    const timer = timers.current[id]
    if (timer) {
      clearTimeout(timer)
      delete timers.current[id]
    }
  }, [])

  const toast = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = nextId++
    setToasts(prev => [...prev, { id, kind, message }])
    timers.current[id] = setTimeout(() => dismiss(id), TOAST_TTL_MS)
  }, [dismiss])

  useEffect(() => {
    return () => {
      for (const timer of Object.values(timers.current)) clearTimeout(timer)
    }
  }, [])

  const value = useMemo<ToastContextValue>(() => ({
    toast,
    success: (m: string) => toast(m, 'success'),
    error: (m: string) => toast(m, 'error'),
    info: (m: string) => toast(m, 'info'),
  }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        maxWidth: 'min(420px, calc(100vw - 3rem))',
      }}
    >
      {toasts.map(t => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  )
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const colorByKind: Record<ToastKind, { bg: string; fg: string; border: string; icon: string }> = {
    success: { bg: 'var(--surface)', fg: 'var(--text)', border: 'var(--green)', icon: '✓' },
    error:   { bg: 'var(--surface)', fg: 'var(--text)', border: 'var(--red)', icon: '⚠' },
    info:    { bg: 'var(--surface)', fg: 'var(--text)', border: 'var(--accent)', icon: 'ℹ' },
  }
  const c = colorByKind[toast.kind]
  return (
    <div
      role={toast.kind === 'error' ? 'alert' : 'status'}
      style={{
        backgroundColor: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        borderRadius: '0.5rem',
        padding: '0.75rem 1rem',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        fontSize: '0.875rem',
        minWidth: '280px',
      }}
    >
      <span aria-hidden="true" style={{ color: c.border, fontWeight: 700 }}>{c.icon}</span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('dismiss')}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          fontSize: '1rem',
          padding: 0,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  )
}
